import {OptInMetadata} from "../../../util/types.ts";
import {useCustomer} from "../../../layouts/SelfcareLayout.tsx";
import {useCallback, useEffect, useState} from "react";
import {confirmOptIn, createOptIn, getOptIn} from "../../../util/request.ts";
import LoaderSpinner from "../../loaders/LoaderSpinner.tsx";
import {phoneNumber} from "../../../util/formatter.ts";
import Icon from "../Icon.tsx";
import IconArrow from "../../icons/IconArrow.tsx";

export default function OptIn({customer_id, type, method, children, header}: {
    customer_id: string;
    type: string;
    method: string;
    children: React.ReactNode;
    header: React.ReactNode;
}) {
    const {customer} = useCustomer()

    const [optIn, setOptIn] = useState<OptInMetadata>()
    const [optInConfirmed, setOptInConfirmed] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false)

    useEffect(() => {
        getOptIn(customer_id, type)
            .then((r) => {
                setOptIn(r.response.data)
                if (r.response.data?.confirmed) {
                    setOptInConfirmed(true)
                }
            })
            .catch(() => {
                setError(true)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [customer_id, type]);

    const handleCreateOptIn = useCallback(() => {
        setLoading(true)
        createOptIn(customer_id, type, method)
            .then((r) => {
                setOptIn(r.response.data);
            })
            .catch(() => {
                setError(true)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [customer_id, type, method])

    if (loading) {
        return <div className={'selfcare-wifi-opt-in'}>
            {header}
            <LoaderSpinner/>
        </div>;
    }

    if (error) {
        return <div className={'selfcare-wifi-opt-in'}>
            {header}
            <p className={'error-message'}>Noget gik galt. Genindlæs siden eller prøv igen senere.</p>
        </div>;
    }

    if (optInConfirmed) {
        return <>{children}</>
    }

    return <div className={'selfcare-wifi-opt-in'}>
        {header}
        {optIn && optIn.id !== null ? (
                <ConfirmOptInForm customer_id={customer_id} optInId={optIn.id} setOptInConfirmed={setOptInConfirmed} createOptIn={handleCreateOptIn}/>
            ) : (
                <>
                    <p className={'align-center'}>
                        Af GDPR-hensyn skal du først bekræfte din identitet med en {method}-kode.
                        Vi sender koden til {phoneNumber(customer.customer_contact.mobile_no)}.
                    </p>
                    <p className={'align-center'}>
                        <button
                            className={'button button--montserrat teal rounded'}
                            onClick={handleCreateOptIn}
                        >Send {method}-kode
                        </button>
                    </p>
                </>
            )
        }
    </div>
}

function ConfirmOptInForm({customer_id, optInId, setOptInConfirmed, createOptIn}: {
    customer_id: string;
    optInId: number,
    setOptInConfirmed: React.Dispatch<React.SetStateAction<boolean>>;
    createOptIn: () => void
}) {
    const [verificationCode, setVerificationCode] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [allRetriesUsed, setAllRetriesUsed] = useState<boolean>(false);

    const handleConfirmOptIn = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);
        confirmOptIn(customer_id, optInId, verificationCode)
            .then((r) => {
                if (r.code === 429) {
                    setAllRetriesUsed(true);
                    return;
                }
                if (r.code === 200 && r.response.data) {
                    setOptInConfirmed(true);
                    setError('')
                } else {
                    setError('Koden var forkert')
                }
            })
            .catch(() => {
                setError('Koden var forkert')
            })
            .finally(() => {
                setLoading(false);
            });
    }

    if (loading) {
        return <LoaderSpinner/>;
    }

    if (allRetriesUsed) {
        return (
            <p className={'error-message'}>
                Du har opbrugt alle dine forsøg. Opret en ny kode ved at{' '}
                <button
                    onClick={createOptIn}
                    type={'button'}
                    className={'link'}
                >trykke her
                </button>
                .
            </p>
        );
    }

    return (
        <>
            {error && <p className={'error-message'}>{error}</p>}
            <form onSubmit={handleConfirmOptIn}>
                <p>Du modtager en kode på sms inden for 2 minutter.</p>
                <div className={'form-item'}>
                    <input
                        type="text"
                        placeholder="Indtast kode"
                        onChange={(e) => setVerificationCode(e.target.value)}
                        required
                    />
                    <label id="submit-form-button">
                        <input disabled={loading} className="continue-button-submit" type="submit"/>
                        <Icon component={IconArrow} className={"continue-button"}/>
                    </label>
                </div>
                <p className={'small'}>
                    Har du ikke modtaget en kode?{' '}
                    <button
                        onClick={createOptIn}
                        type='button'
                        className={'link'}
                    >
                        Tryk her for at få en ny kode.
                    </button>
                </p>
            </form>
        </>
    );
}
